/**
 * `/policy` — the four documents, as a list.
 *
 * NO DRAWN FRAME EXISTS FOR THIS SCREEN. `docs/11-screen-map.md` has one policy row and
 * it is H2, the refund sheet that opens from checkout. So this is not a transcription
 * and does not claim to be one: it is built from `@oprappo/tokens`' palette, the four
 * fonts, the 13px Bangla floor and the flat surfaces the design system already uses
 * everywhere else — and it renders not one word this repository wrote.
 *
 * Reported, not hidden: a designer should draw this screen and H2's siblings. Listed in
 * `docs/bangla-needed/legal-documents.md § screens nobody drew`. It is deliberately
 * absent from `apps/web/src/route-manifest.ts` (which this stream does not own), so the
 * visual gate has no row for it and cannot report a pass it did not measure.
 *
 * ── EVERY STRING HERE COMES FROM THE SERVER ─────────────────────────────────────
 * Titles, the intro, the badge, the row labels — all of it is in `chrome` and `items` on
 * the response. `__tests__/policy.test.tsx` scans this file for Bangla characters and
 * fails on any, so the rule is measured rather than promised.
 */
import { Link } from 'react-router-dom';
import { FRAME_BOX_DOCUMENT } from '../../responsive/frame.js';
import { useResource } from '../../data/resource.js';
import { SurfaceStates } from '../../states/boundary.js';
import { RowChevron, ScreenHeader } from '../offering/chrome.js';
import { fetchPolicyIndex, type PolicyIndexResponse } from './api.js';

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD. The view renders what
 * `GET /v1/policy/documents` returned; the container below does the loading.
 *
 * The split is the one `routes/explore/explore.tsx` already makes, and here it earns its
 * keep twice: `__tests__/policy.test.tsx` renders this from the same fixture the visual
 * gate serves, and it can only assert "the draft notice renders" against a component
 * that will render without a network.
 */
export function PolicyIndexView({ data }: { data: PolicyIndexResponse | null }): JSX.Element {
  return (
    <div className={`bg-sand ${FRAME_BOX_DOCUMENT} border border-border`}>
        {/*
          `onBack` is omitted rather than wired to `navigate(-1)`. A signed-out visitor
          can reach this page from a link in a message, where there is no history entry
          to go back to and `-1` leaves the browser on the page it was already on. The
          control renders and does nothing visible, which is what every other screen's
          back arrow does at the start of a history stack.
        */}
        <ScreenHeader titleBn={data?.chrome.index_title_bn ?? ''} />

        <div className="px-[16px] pt-[16px] pb-[24px] flex-1">
          <p className="m-0 font-anek font-normal text-[14px] leading-[1.75] text-body">
            {data?.chrome.index_intro_bn ?? ''}
          </p>

          <div className="mt-[16px] bg-white border border-border">
            {(data?.items ?? []).map((item, i) => (
              <Link
                key={item.slug}
                to={item.to}
                className={
                  'flex gap-[12px] items-start px-[14px] py-[13px] no-underline' +
                  (i === 0 ? '' : ' border-t border-border')
                }
              >
                <div className="flex-1 min-w-0">
                  <div className="flex gap-[8px] items-center">
                    <span className="font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                      {item.title_bn}
                    </span>
                    {/*
                      THE DRAFT BADGE, on every row whose document no lawyer has read.
                      Rendered from `review_status` rather than from a flag the client
                      keeps: the screen cannot show a document as reviewed unless the
                      server says it is.

                      `warning-surface` on `amber-text` — the amber-on-light token, which
                      docs/02 admits as readable text where plain amber is not.
                    */}
                    {item.review_status === 'unreviewed_draft' ? (
                      <span className="font-anek font-medium text-[13px] leading-[1.75] text-amber-text bg-warning-surface border border-warning-border px-[7px]">
                        {data?.chrome.draft_badge_bn ?? ''}
                      </span>
                    ) : null}
                  </div>
                  <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[2px]">
                    {item.summary_bn}
                  </div>
                  <div className="font-manrope font-medium text-[13px] leading-[1.75] text-muted mt-[4px] [font-variant-numeric:tabular-nums]">
                    {/*
                      The version and the date, in Manrope because they are Latin and
                      numeric. NOT composed with a Bangla word: the label lives in
                      `chrome` and is rendered beside them, never concatenated into one
                      string here (docs/05 § the client never composes Bangla).
                    */}
                    <span className="font-anek font-normal text-muted">
                      {data?.chrome.version_label_bn ?? ''}
                    </span>{' '}
                    {item.version} · {item.effective_from}
                  </div>
                </div>
                <div className="pt-[4px]">
                  <RowChevron size={20} />
                </div>
              </Link>
            ))}
          </div>
        </div>
    </div>
  );
}

/** The container: loads once, and hands whatever came back to the view. */
export function PolicyIndex(): JSX.Element {
  const { data, error } = useResource((signal) => fetchPolicyIndex(signal), 'policy-index');
  return (
    <SurfaceStates surface="onboarding" error={error ?? undefined}>
      <PolicyIndexView data={data} />
    </SurfaceStates>
  );
}
